import { z } from "zod";

export const GeographySchema = z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    type: z.enum(["country", "region", "global", "residual"]),
});

export const GeographyArraySchema = z.array(GeographySchema);

export type Geography = z.infer<typeof GeographySchema>;

export const geographySearchApiKey = "geographySearch";
export const getOneGeographyApiKey = "getOneGeography";

export const formatGeographyLabel = (geography: Geography): string => `${geography.code} — ${geography.name}`;

export const looksLikeGeographyCode = (input: string): boolean => /^[A-Za-z0-9-]{1,20}$/.test(input.trim());

export const mergeGeographyOptions = (...groups: (Geography | null | undefined)[][]): Geography[] => {
    const seen = new Set<string>();
    const options: Geography[] = [];

    groups.flat().forEach((geography) => {
        if (!geography || seen.has(geography.code)) {
            return;
        }

        seen.add(geography.code);
        options.push(geography);
    });

    return options;
};

export const resolveGeographyOption = (
    origin: string | null | undefined,
    geographies: Geography[],
): Geography | null => {
    if (origin == null || origin.trim() === "") {
        return null;
    }

    const normalized = origin.trim().toUpperCase();

    return (
        geographies.find((geography) => geography.code.toUpperCase() === normalized) ??
        geographies.find((geography) => geography.name.toUpperCase() === normalized) ??
        null
    );
};

const compareGeographyByName = (left: Geography, right: Geography): number =>
    left.name.localeCompare(right.name, undefined, { sensitivity: "base" });

/** Origin picker order: all regions (A→Z), then all countries (A→Z). */
export const sortOriginGeographyOptions = (geographies: Geography[]): Geography[] => {
    const regions = geographies.filter((geography) => geography.type === "region").sort(compareGeographyByName);
    const countries = geographies.filter((geography) => geography.type === "country").sort(compareGeographyByName);

    return [...regions, ...countries];
};

export const buildProvisionalGeographyOption = (origin: string): Geography => {
    const trimmed = origin.trim();
    const code = trimmed.toUpperCase();

    return {
        id: 0,
        code,
        name: trimmed,
        type: code.length === 2 ? "country" : "region",
    };
};
